import { alpha } from '@mui/material'
import { green, red } from '../../assets/colors'
import { formatGreeks } from '../../pages/home/optionChainViewTabs/components/DynamicColumnRender'
import { toLocaleStringIn } from '../../util/general'
import { formatPayoffOiScalePrice } from '../../util/payoffChart'
import { LiveTextSelector } from '../common/LiveText'
import { getPayoffGraphMetaData, subscribePayoffGraphMetaData } from './payoffGraphMetaDataStore'

const displaySelectorCache = new Map<string, LiveTextSelector>()
const colorSelectorCache = new Map<string, LiveTextSelector>()

const getCachedSelector = (
    cache: Map<string, LiveTextSelector>,
    cacheKey: string,
    factory: () => LiveTextSelector
): LiveTextSelector => {
    if (!cache.has(cacheKey)) {
        cache.set(cacheKey, factory())
    }
    return cache.get(cacheKey)!
}

const formatPnl = (value: number): string => {
    if (!Number.isFinite(value)) return '-'
    const formatted = toLocaleStringIn(value)
    return value > 0 ? `+${formatted}` : formatted
}

const formatDelta = (delta: number): string => {
    if (!Number.isFinite(delta) || delta === 0) return '-'
    const formatted = formatGreeks(delta)
    return formatted === '-' ? '-' : `δ${formatted}`
}

const pnlColor = (value: number): string => {
    if (!Number.isFinite(value) || value === 0) return 'inherit'
    return value > 0 ? green : red
}

export const buildPayoffGraphMetaLiveSubscribe = (metaDataKey: string) => {
    return (listener: () => void) => subscribePayoffGraphMetaData(metaDataKey, listener)
}

export const getPayoffStrikeSelector = (metaDataKey: string): LiveTextSelector =>
    getCachedSelector(displaySelectorCache, `${metaDataKey}:strike`, () => () => {
        const meta = getPayoffGraphMetaData(metaDataKey)
        return meta ? String(meta.time) : ''
    })

export const getPayoffCallOiSelector = (metaDataKey: string): LiveTextSelector =>
    getCachedSelector(displaySelectorCache, `${metaDataKey}:callOi`, () => () => {
        const meta = getPayoffGraphMetaData(metaDataKey)
        return meta ? formatPayoffOiScalePrice(meta.callOi) : ''
    })

export const getPayoffCallDeltaSelector = (metaDataKey: string): LiveTextSelector =>
    getCachedSelector(displaySelectorCache, `${metaDataKey}:callDelta`, () => () => {
        const meta = getPayoffGraphMetaData(metaDataKey)
        return meta ? formatDelta(meta.callDelta) : ''
    })

export const getPayoffPutOiSelector = (metaDataKey: string): LiveTextSelector =>
    getCachedSelector(displaySelectorCache, `${metaDataKey}:putOi`, () => () => {
        const meta = getPayoffGraphMetaData(metaDataKey)
        return meta ? formatPayoffOiScalePrice(meta.putOi) : ''
    })

export const getPayoffPutDeltaSelector = (metaDataKey: string): LiveTextSelector =>
    getCachedSelector(displaySelectorCache, `${metaDataKey}:putDelta`, () => () => {
        const meta = getPayoffGraphMetaData(metaDataKey)
        return meta ? formatDelta(meta.putDelta) : ''
    })

export const getPayoffExpiryPnlSelector = (metaDataKey: string): LiveTextSelector =>
    getCachedSelector(displaySelectorCache, `${metaDataKey}:expiryPnl`, () => () => {
        const meta = getPayoffGraphMetaData(metaDataKey)
        return meta ? formatPnl(meta.expiryPnl) : ''
    })

export const getPayoffExpectedPnlSelector = (metaDataKey: string): LiveTextSelector =>
    getCachedSelector(displaySelectorCache, `${metaDataKey}:expectedPnl`, () => () => {
        const meta = getPayoffGraphMetaData(metaDataKey)
        return meta ? formatPnl(meta.expectedPnl) : ''
    })

export const getPayoffExpiryPnlColorSelector = (metaDataKey: string): LiveTextSelector =>
    getCachedSelector(colorSelectorCache, `${metaDataKey}:expiryPnlColor`, () => () => {
        const meta = getPayoffGraphMetaData(metaDataKey)
        return meta ? pnlColor(meta.expiryPnl) : ''
    })

export const getPayoffExpectedPnlColorSelector = (metaDataKey: string): LiveTextSelector =>
    getCachedSelector(colorSelectorCache, `${metaDataKey}:expectedPnlColor`, () => () => {
        const meta = getPayoffGraphMetaData(metaDataKey)
        return meta ? pnlColor(meta.expectedPnl) : ''
    })

export const getPayoffCallDeltaColorSelector = (metaDataKey: string, callColor: string): LiveTextSelector =>
    getCachedSelector(colorSelectorCache, `${metaDataKey}:callDeltaColor:${callColor}`, () => () =>
        alpha(callColor, 0.72)
    )

export const getPayoffPutDeltaColorSelector = (metaDataKey: string, putColor: string): LiveTextSelector =>
    getCachedSelector(colorSelectorCache, `${metaDataKey}:putDeltaColor:${putColor}`, () => () =>
        alpha(putColor, 0.72)
    )
